import React, {useEffect, useRef, useState} from 'react'
import {createAvatar, deleteAvatar, getAllAvatars} from '../../../../../../API/api-endpoint'
import {toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import clsx from 'clsx'
import ToastUtils, {ErrorToastUtils} from '../../../../../../utils/ToastUtils'
import {getImageBaseUrl} from '../../../../../../utils/Utils'
import Swal from 'sweetalert2'

const AvatarSettingPlugin = () => {
  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const [avatarList, setAvatarList] = useState<any>([])
  const [currentImageTempPath, setCurrentImageTempPath] = useState<string>('')
  const [isImageUploaded, setIsImageUploaded] = useState<boolean>(false)
  const [file, setFile] = useState<any>(null)
  const [gender, setGender] = useState<string>('male')
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    fetchAvatars()
  }, [])

  const fetchAvatars = async () => {
    let result = await getAllAvatars()
    if (result.success) {
      setAvatarList(result.data)
    }
  }

  const handleClick = () => {
    hiddenFileInput.current?.click()
  }

  const handleIconChange = (event: any) => {
    const fileUploaded = event.target.files[0]
    if (fileUploaded) {
      const tmppath = URL.createObjectURL(fileUploaded)
      setCurrentImageTempPath(tmppath)
      setFile(fileUploaded)
      setIsImageUploaded(true)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      ToastUtils({type: 'error', message: 'Please select an image'})
      return
    }

    setLoading(true)
    let result = await createAvatar(gender, file)
    setLoading(false)

    if (result.success) {
      fetchAvatars()
      ToastUtils({type: 'success', message: 'Avatar uploaded successfully'})
      resetForm()
    } else {
      ErrorToastUtils()
    }
  }

  const handleDelete = async (avatarId: string) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (swalResult) => {
      if (swalResult.isConfirmed) {
        let result = await deleteAvatar(avatarId)
        if (result.success) {
          fetchAvatars()
          ToastUtils({type: 'success', message: 'Avatar deleted successfully'})
        } else {
          ErrorToastUtils()
        }
      }
    })
  }

  const resetForm = () => {
    setFile(null)
    setCurrentImageTempPath('')
    setIsImageUploaded(false)
    setGender('male')
  }

  let baseImageUrl = getImageBaseUrl()

  const maleAvatars = avatarList.filter((a: any) => a.gender === 'male')
  const femaleAvatars = avatarList.filter((a: any) => a.gender === 'female')

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 cursor-pointer'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Avatar Settings</h3>
        </div>
      </div>

      <div className='card-body border-top p-9'>
        <div className='row mb-10'>
          <label className='col-lg-4 col-form-label fw-bold fs-6'>Upload New Avatar</label>
          <div className='col-lg-8'>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-100px symbol-lg-150px me-7'>
                <div
                  className='cursor-pointer'
                  onClick={handleClick}
                  style={{
                    width: '150px',
                    height: '150px',
                    border: '2px dashed #009ef7',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f1faff',
                    overflow: 'hidden',
                  }}
                >
                  {isImageUploaded ? (
                    <img
                      src={currentImageTempPath}
                      alt='Avatar Preview'
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                    />
                  ) : (
                    <div className='text-center'>
                      <i className='bi bi-plus-lg fs-1 text-primary'></i>
                      <div className='mt-2 text-primary fw-bold'>Select Image</div>
                    </div>
                  )}
                </div>
                <input
                  type='file'
                  ref={hiddenFileInput}
                  onChange={handleIconChange}
                  style={{display: 'none'}}
                  accept='image/*'
                />
              </div>

              <div className='flex-grow-1'>
                <div className='mb-7'>
                  <label className='form-label fw-bold fs-6'>Gender</label>
                  <div className='d-flex align-items-center mt-3'>
                    <label className='form-check form-check-custom form-check-solid me-10'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='gender'
                        value='male'
                        checked={gender === 'male'}
                        onChange={(e) => setGender(e.target.value)}
                      />
                      <span className='form-check-label fw-bold'>Male</span>
                    </label>
                    <label className='form-check form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='gender'
                        value='female'
                        checked={gender === 'female'}
                        onChange={(e) => setGender(e.target.value)}
                      />
                      <span className='form-check-label fw-bold'>Female</span>
                    </label>
                  </div>
                </div>

                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={loading || !file}
                  onClick={handleUpload}
                >
                  {loading ? 'Uploading...' : 'Upload Avatar'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='separator separator-dashed my-10'></div>

        <div className='row'>
          <div className='col-lg-6'>
            <h4 className='fw-bolder mb-7'>Male Avatars ({maleAvatars.length})</h4>
            <div className='d-flex flex-wrap gap-5'>
              {maleAvatars.map((avatar: any) => (
                <div key={avatar.avatarId} className='position-relative symbol symbol-75px'>
                  <img src={`${baseImageUrl}/${avatar.image}`} alt='Avatar' />
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm position-absolute top-0 end-0'
                    style={{transform: 'translate(50%, -50%)'}}
                    onClick={() => handleDelete(avatar.avatarId)}
                  >
                    <i className='bi bi-trash fs-5'></i>
                  </button>
                </div>
              ))}
              {maleAvatars.length === 0 && <p className='text-muted'>No male avatars yet.</p>}
            </div>
          </div>

          <div className='col-lg-6 mt-10 mt-lg-0'>
            <h4 className='fw-bolder mb-7'>Female Avatars ({femaleAvatars.length})</h4>
            <div className='d-flex flex-wrap gap-5'>
              {femaleAvatars.map((avatar: any) => (
                <div key={avatar.avatarId} className='position-relative symbol symbol-75px'>
                  <img src={`${baseImageUrl}/${avatar.image}`} alt='Avatar' />
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm position-absolute top-0 end-0'
                    style={{transform: 'translate(50%, -50%)'}}
                    onClick={() => handleDelete(avatar.avatarId)}
                  >
                    <i className='bi bi-trash fs-5'></i>
                  </button>
                </div>
              ))}
              {femaleAvatars.length === 0 && <p className='text-muted'>No female avatars yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AvatarSettingPlugin
